(() => {
  const QUERY_PARAMETER = 'featured_product';
  const STORAGE_KEY = 'tpgFeaturedProduct';
  const STORAGE_DURATION = 24 * 60 * 60 * 1000;

  class TPGFeaturedProduct {
    constructor(container) {
      if (container.tpgFeaturedProductController) return;

      container.tpgFeaturedProductController = this;
      this.container = container;
      this.featuredItem = null;
      this.fetchPromise = null;
      this.pinScheduled = false;

      const selection = this.getSelection();
      if (!selection) return;

      this.handle = selection.handle;
      this.fromQueryParameter = selection.fromQueryParameter;
      this.renderer = container.dataset.featuredProductRenderer;

      if (!this.renderer) return;

      this.observer = new MutationObserver((mutations) => {
        if (this.hasGridMutation(mutations)) this.schedulePin();
      });
      this.observer.observe(this.container, { childList: true, subtree: true });

      this.ensurePinned().then((wasPinned) => {
        if (wasPinned) {
          if (this.fromQueryParameter) this.storeSelection(this.handle);
        } else {
          this.clearStoredSelection();
        }
      });
    }

    getSelection() {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has(QUERY_PARAMETER)) {
        const handle = this.normalizeHandle(searchParams.get(QUERY_PARAMETER));

        if (!handle) {
          this.clearStoredSelection();
          return null;
        }

        return { handle, fromQueryParameter: true };
      }

      const storedHandle = this.getStoredSelection();
      return storedHandle ? { handle: storedHandle, fromQueryParameter: false } : null;
    }

    normalizeHandle(value) {
      const handle = typeof value === 'string' ? value.trim().toLowerCase() : '';
      return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) ? handle : null;
    }

    getStoredSelection() {
      try {
        const storedValue = window.localStorage.getItem(STORAGE_KEY);
        if (!storedValue) return null;

        const selection = JSON.parse(storedValue);
        const handle = this.normalizeHandle(selection.handle);

        if (!handle || !Number.isFinite(selection.expiresAt) || selection.expiresAt <= Date.now()) {
          this.clearStoredSelection();
          return null;
        }

        return handle;
      } catch (error) {
        return null;
      }
    }

    storeSelection(handle) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
          handle,
          expiresAt: Date.now() + STORAGE_DURATION
        }));
      } catch (error) {
        // Storage can be unavailable in private browsing; pinning still works for the current page.
      }
    }

    clearStoredSelection() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // Ignore unavailable storage and leave the current page functional.
      }
    }

    getGrid() {
      return this.container.querySelector('[id="data-pagination"]');
    }

    hasGridMutation(mutations) {
      const grid = this.getGrid();

      return mutations.some((mutation) => {
        if (mutation.target === grid) return true;

        return Array.from(mutation.addedNodes).some((node) => {
          return node.nodeType === Node.ELEMENT_NODE && (
            node.id === 'data-pagination' || node.querySelector('[id="data-pagination"]')
          );
        });
      });
    }

    schedulePin() {
      if (this.pinScheduled) return;

      this.pinScheduled = true;
      window.requestAnimationFrame(() => {
        this.pinScheduled = false;
        this.ensurePinned();
      });
    }

    async ensurePinned() {
      const grid = this.getGrid();
      if (!grid) return false;

      const matchingItems = Array.from(grid.children).filter((item) => {
        return item.dataset.productHandle === this.handle;
      });

      let item = matchingItems[0] || this.featuredItem;

      if (!item) {
        item = await this.fetchFeaturedItem();
        if (!item) return false;
      }

      this.featuredItem = item;

      Array.from(grid.children).forEach((gridItem) => {
        if (gridItem !== item && gridItem.dataset.productHandle === this.handle) {
          gridItem.remove();
        }
      });

      const placementPromo = Array.from(grid.children).find((gridItem) => {
        return 'tpgPromoPlacement' in gridItem.dataset;
      });

      if (placementPromo) {
        if (placementPromo.nextElementSibling !== item) placementPromo.after(item);
      } else if (grid.firstElementChild !== item) {
        grid.prepend(item);
      }

      return true;
    }

    fetchFeaturedItem() {
      if (this.fetchPromise) return this.fetchPromise;

      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      const productUrl = new URL(`${root}products/${encodeURIComponent(this.handle)}`, window.location.origin);
      productUrl.searchParams.set('section_id', this.renderer);

      this.fetchPromise = fetch(productUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to render featured product: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const item = html.querySelector('[data-featured-product-item][data-product-handle]');
          if (!item) return null;

          const renderedHandle = this.normalizeHandle(item.dataset.productHandle);
          if (!renderedHandle) return null;

          this.handle = renderedHandle;

          if (this.container.dataset.showFinancing !== 'true') {
            item.querySelector('.tpg-card__financing')?.remove();
          }

          return document.importNode(item, true);
        })
        .catch(() => null);

      return this.fetchPromise;
    }
  }

  const initializeFeaturedProducts = (scope = document) => {
    scope.querySelectorAll('product-grid-container[data-tpg-featured-product]').forEach((container) => {
      new TPGFeaturedProduct(container);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeFeaturedProducts());
  } else {
    initializeFeaturedProducts();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeFeaturedProducts(event.target);
  });
})();
